import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { fmtError } from "@/lib/api";
import { fmtDate, fmtINR } from "@/lib/format";
import { INDUSTRY_OPTIONS } from "@/lib/industries";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Search, Building2, Plus, Upload as UploadIcon, ExternalLink, MapPin, Mail, Phone as PhoneIcon, Hash, Pencil } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import EmployeeFilter from "@/components/EmployeeFilter";
import CustomersTabs from "@/components/CustomersTabs";
import StateDistrictPicker from "@/components/StateDistrictPicker";
import StateDistrictFilter from "@/components/StateDistrictFilter";
import { useDuplicateCheck } from "@/components/DuplicateContactDialog";
import CallButton from "@/components/CallButton";
import WhatsAppButton from "@/components/WhatsAppButton";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";

const TYPES = [
  { v: "new_lead", l: "New Lead" },
  { v: "existing", l: "Existing" },
  { v: "repeat", l: "Repeat" },
  { v: "amc", l: "AMC" },
  { v: "service", l: "Service" },
];

function NewClientDialog({ open, onOpenChange, onCreated, initial = null }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({
    company_name: "", contact_person: "", industry: "", location: "", state: "", district: "",
    email: "", phone: "", customer_type: "new_lead", notes: "",
    gst_number: "", state_code: "", state_name: "",
    billing_address: { attention: "", line1: "", line2: "", city: "", state: "", phone: "" },
  });
  const [saving, setSaving] = useState(false);
  // Cross-entity fuzzy duplicate check — same hook used by Leads/Contacts.
  // Surfaces a popup when a near-match exists in any people collection
  // (contacts, leads, marketing, clients) before we create/update.
  const dup = useDuplicateCheck("client");

  useEffect(() => {
    if (open) {
      if (initial) {
        const ba = initial.billing_address || {};
        setForm({
          company_name: initial.company_name || "",
          contact_person: initial.contact_person || "",
          industry: initial.industry || "",
          location: initial.location || "",
          state: initial.state || "",
          district: initial.district || "",
          email: initial.email || "",
          phone: initial.phone || "",
          customer_type: initial.customer_type || "new_lead",
          notes: initial.notes || "",
          gst_number: initial.gst_number || "",
          state_code: initial.state_code || "",
          state_name: initial.state_name || "",
          billing_address: {
            attention: ba.attention || "", line1: ba.line1 || "", line2: ba.line2 || "",
            city: ba.city || "", state: ba.state || "", phone: ba.phone || "",
          },
        });
      } else {
        setForm({
          company_name: "", contact_person: "", industry: "", location: "", state: "", district: "",
          email: "", phone: "", customer_type: "new_lead", notes: "",
          gst_number: "", state_code: "", state_name: "",
          billing_address: { attention: "", line1: "", line2: "", city: "", state: "", phone: "" },
        });
      }
    }
  }, [open, initial]);

  const setBA = (k, v) => setForm((f) => ({ ...f, billing_address: { ...f.billing_address, [k]: v } }));
  const submit = async () => {
    if (!form.company_name) { toast.error("Company name required"); return; }
    const doSave = async () => {
      setSaving(true);
      try {
        // Strip empty billing_address sub-fields cleanly.
        const ba = form.billing_address || {};
        const baClean = Object.fromEntries(Object.entries(ba).filter(([, v]) => v && String(v).trim()));
        const payload = {
          ...form,
          billing_address: Object.keys(baClean).length ? baClean : null,
        };
        if (isEdit) {
          await api.patch(`/clients/${initial.id}`, payload);
          toast.success("Client updated");
        } else {
          await api.post("/clients", payload);
          toast.success("Client added");
        }
        onCreated?.();
        onOpenChange(false);
      } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
    };
    // Skip the duplicate check when EDITING an existing client — we expect
    // the same record to "match itself". Only gate the create flow.
    if (isEdit) return doSave();
    dup.guard({
      name: form.contact_person,
      email: form.email,
      phone: form.phone,
      company_name: form.company_name,
    }, doSave);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold">{isEdit ? "Edit client" : "New client"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs font-semibold">Company name *</Label>
            <Input className="rounded-2xl h-11 mt-1.5" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="client-name-input" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs font-semibold">Primary contact person</Label>
            <Input className="rounded-2xl h-11 mt-1.5" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} placeholder="e.g. Rajeev Kumar" data-testid="client-contact-person-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Industry</Label>
            <Input
              list="renviro-industries"
              className="rounded-2xl h-11 mt-1.5"
              value={form.industry}
              onChange={(e) => setForm({ ...form, industry: e.target.value })}
              placeholder="Type or pick from list"
              data-testid="client-industry"
            />
            <datalist id="renviro-industries">
              {INDUSTRY_OPTIONS.map((i) => (<option key={i} value={i} />))}
            </datalist>
          </div>
          <div className="col-span-2">
            <StateDistrictPicker
              state={form.state || ""}
              district={form.district || ""}
              onChange={({ state, district }) => setForm({ ...form, state, district })}
              testId="client"
            />
          </div>
          <div><Label className="text-xs font-semibold">Email</Label><Input className="rounded-2xl h-11 mt-1.5" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label className="text-xs font-semibold">Phone</Label><Input className="rounded-2xl h-11 mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div><Label className="text-xs font-semibold">GSTIN</Label><Input className="rounded-2xl h-11 mt-1.5" value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value })} placeholder="27ABCDE1234F1Z5" data-testid="client-gst-input" /></div>
          <div><Label className="text-xs font-semibold">State code</Label><Input className="rounded-2xl h-11 mt-1.5" value={form.state_code} onChange={(e) => setForm({ ...form, state_code: e.target.value })} placeholder="33" data-testid="client-state-code-input" /></div>
          <div className="col-span-2"><Label className="text-xs font-semibold">State name (Place of supply)</Label><Input className="rounded-2xl h-11 mt-1.5" value={form.state_name} onChange={(e) => setForm({ ...form, state_name: e.target.value })} placeholder="e.g. Tamil Nadu" data-testid="client-state-name-input" /></div>
          <div className="col-span-2 mt-1">
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">Billing address</p>
          </div>
          <div className="col-span-2"><Label className="text-xs font-semibold">Billing attention</Label><Input className="rounded-2xl h-11 mt-1.5" value={form.billing_address.attention} onChange={(e) => setBA("attention", e.target.value)} placeholder="Accounts Payable" /></div>
          <div className="col-span-2"><Label className="text-xs font-semibold">Address line 1</Label><Input className="rounded-2xl h-11 mt-1.5" value={form.billing_address.line1} onChange={(e) => setBA("line1", e.target.value)} /></div>
          <div className="col-span-2"><Label className="text-xs font-semibold">Street 2</Label><Input className="rounded-2xl h-11 mt-1.5" value={form.billing_address.line2} onChange={(e) => setBA("line2", e.target.value)} /></div>
          <div><Label className="text-xs font-semibold">Billing city</Label><Input className="rounded-2xl h-11 mt-1.5" value={form.billing_address.city} onChange={(e) => setBA("city", e.target.value)} /></div>
          <div><Label className="text-xs font-semibold">Billing state</Label><Input className="rounded-2xl h-11 mt-1.5" value={form.billing_address.state} onChange={(e) => setBA("state", e.target.value)} /></div>
          <div className="col-span-2"><Label className="text-xs font-semibold">Billing phone</Label><Input className="rounded-2xl h-11 mt-1.5" value={form.billing_address.phone} onChange={(e) => setBA("phone", e.target.value)} /></div>
          <div className="col-span-2">
            <Label className="text-xs font-semibold">Type</Label>
            <Select value={form.customer_type} onValueChange={(v) => setForm({ ...form, customer_type: v })}>
              <SelectTrigger className="rounded-2xl h-11 mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-2xl">{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs font-semibold">Notes</Label>
            <Textarea className="rounded-2xl mt-1.5" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-2xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-2xl" data-testid="client-save-button">{saving ? "Saving…" : (isEdit ? "Save changes" : "Add client")}</Button>
        </DialogFooter>
        <dup.Dialog />
      </DialogContent>
    </Dialog>
  );
}

export default function Clients() {
  const [params] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filterType, setFilterType] = useState("");
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(params.get("new") === "1");
  const [editing, setEditing] = useState(null); // client object being edited
  const [openImport, setOpenImport] = useState(false);
  const [ownerId, setOwnerId] = useState("");
  const [mine, setMine] = useState(false);
  // Geographical scoping — mirrors Leads / Contacts so the filter chip in
  // the toolbar actually has a state variable to read from. Without these,
  // the page crashes with `ReferenceError: stateFilter is not defined`.
  const [stateFilter, setStateFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients", { params: {
        q: q || undefined,
        customer_type: filterType || undefined,
        owner_id: mine ? undefined : (ownerId || undefined),
        mine: mine || undefined,
        state: stateFilter || undefined,
        district: districtFilter || undefined,
      } });
      setRows(data);
    } finally { setLoading(false); }
  }, [q, filterType, ownerId, mine, stateFilter, districtFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  const { indicator: pullIndicator } = usePullToRefresh(load);

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-[1280px] mx-auto">
      {pullIndicator}
      <CustomersTabs />
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">Customer database</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">All your clients.</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setOpenImport(true)} className="rounded-2xl tap" data-testid="clients-import-button">
            <UploadIcon className="h-4 w-4 mr-1.5" /> Import CSV
          </Button>
          <Button onClick={() => setOpenNew(true)} className="rounded-2xl tap" data-testid="new-client-button">
            <Plus className="h-4 w-4 mr-1" /> New client
          </Button>
        </div>
      </header>

      <div className="flex gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search company, email, phone, location, industry…"
            className="pl-10 h-12 rounded-2xl bg-muted/40"
            data-testid="clients-search-input"
          />
        </div>
        <Select value={filterType || "__all"} onValueChange={(v) => setFilterType(v === "__all" ? "" : v)}>
          <SelectTrigger className="rounded-2xl h-12 w-44" data-testid="clients-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent className="rounded-2xl">
            <SelectItem value="__all">All types</SelectItem>
            {TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <EmployeeFilter value={ownerId} onChange={(v) => { setOwnerId(v); if (v) setMine(false); }} testId="clients-employee-filter" />
        <StateDistrictFilter
          state={stateFilter}
          district={districtFilter}
          onChange={({ state, district }) => { setStateFilter(state); setDistrictFilter(district); }}
          testId="clients-state-filter"
        />
        <button
          type="button"
          onClick={() => { setMine((m) => !m); if (!mine) setOwnerId(""); }}
          className={`tap rounded-2xl h-12 px-4 text-sm font-semibold transition border ${
            mine
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-muted/40 border-border/60 hover:bg-muted/60"
          }`}
          data-testid="clients-mine-toggle"
          title="Show only clients you own"
        >
          Mine only
        </button>
      </div>

      <p className="text-xs text-muted-foreground -mt-2 mb-4">
        Company-wide directory — everyone with view access sees all clients. Use <strong>Mine only</strong> or the employee filter to narrow. Edits are still gated to the owner + admins.
      </p>

      {loading ? (
        <div className="space-y-3">
          {["s1", "s2", "s3", "s4", "s5", "s6"].map((k) => <Skeleton key={k} className="h-20 rounded-2xl" />)}
        </div>
      ) : rows.length === 0 ? (
        <Card className="rounded-3xl border-border/60 shadow-soft"><CardContent className="p-12 text-center text-sm text-muted-foreground">No clients found.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {rows.map((c) => {
            const ba = c.billing_address || {};
            const phone = c.phone || ba.phone || "";
            const email = c.email || "";
            const website = c.website || "";
            // Prefer the new structured operational location, then GST/billing
            // pair, then the legacy free-form `location`. Order keeps reps' eyes
            // on where the customer's plant actually sits.
            const cityState = (
              [c.district, c.state].filter(Boolean).join(", ") ||
              [ba.city, c.state_name || ba.state].filter(Boolean).join(", ") ||
              c.location || ""
            );
            return (
              <div
                key={c.id}
                data-testid={`client-card-${c.id}`}
                className="relative p-4 rounded-2xl border border-border/60 bg-card hover:bg-muted/30 card-lift transition overflow-hidden"
              >
                <Link to={`/clients/${c.id}`} className="block tap" data-testid={`client-card-link-${c.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="h-11 w-11 rounded-2xl bg-primary-50 text-primary-700 grid place-items-center shrink-0">
                      <Building2 className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">{c.company_name}</p>
                      {/* Decision-makers — prefer the live `contacts_preview`
                          coming from the linked contacts collection (Sunil
                          Kashyap, Sachin Bhatnagar…) over the stale
                          singular `contact_person` stamped at creation.
                          When neither exists, fall back to the legacy field. */}
                      {(c.contacts_preview && c.contacts_preview.length > 0) ? (
                        <p className="text-xs text-muted-foreground truncate mt-0.5" data-testid={`client-card-contacts-${c.id}`}>
                          {c.contacts_preview.map((p) => p.name).filter(Boolean).join(" · ")}
                          {c.contacts_count > c.contacts_preview.length && (
                            <span className="text-muted-foreground/70"> · +{c.contacts_count - c.contacts_preview.length} more</span>
                          )}
                        </p>
                      ) : c.contact_person ? (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{c.contact_person}</p>
                      ) : null}
                      {c.owner_name && (
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground/80 mt-0.5">
                          Owner: <span className="font-semibold text-foreground/80">{c.owner_name}</span>
                        </p>
                      )}
                    </div>
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wider bg-primary-50 text-primary-700 border-primary-200 capitalize shrink-0">
                      {(c.customer_type || "").replace(/_/g, " ")}
                    </Badge>
                  </div>

                  <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                    {phone && (
                      <div className="flex items-center gap-1.5 truncate"><PhoneIcon className="h-3 w-3 shrink-0" /><span className="truncate">{phone}</span></div>
                    )}
                    {(email || website) && (
                      <div className="flex items-center gap-1.5 truncate"><Mail className="h-3 w-3 shrink-0" /><span className="truncate">{email || website}</span></div>
                    )}
                    {cityState && (
                      <div className="flex items-center gap-1.5 truncate"><MapPin className="h-3 w-3 shrink-0" /><span className="truncate">{cityState}</span></div>
                    )}
                    {c.gst_number && (
                      <div className="flex items-center gap-1.5 truncate"><Hash className="h-3 w-3 shrink-0" /><span className="truncate font-mono text-[10px]">{c.gst_number}</span></div>
                    )}
                    {!phone && !email && !website && !cityState && !c.gst_number && (
                      <div className="text-muted-foreground/60 italic">No contact details captured.</div>
                    )}
                  </div>
                </Link>

                <div className="mt-3 pt-3 border-t border-border/60 flex flex-wrap items-center gap-2">
                  <CallButton phone={phone} testId={`client-card-call-${c.id}`} />
                  <WhatsAppButton
                    phone={phone}
                    email={email}
                    contactName={c.contact_person}
                    clientId={c.id}
                    clientCompany={c.company_name}
                    defaultTemplate="intro"
                    label="WhatsApp"
                    testId={`client-card-wa-${c.id}`}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditing(c); }}
                    className="rounded-xl ml-auto h-8 text-xs"
                    data-testid={`client-card-edit-${c.id}`}
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                  </Button>
                  {!phone && !email && (
                    <span className="text-[10px] text-muted-foreground italic">No phone/email — open card to add</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <NewClientDialog open={openNew} onOpenChange={setOpenNew} onCreated={load} />
      <NewClientDialog
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        initial={editing}
        onCreated={() => { setEditing(null); load(); }}
      />
      <ImportClientsDialog open={openImport} onOpenChange={setOpenImport} onDone={load} />
    </div>
  );
}

function ImportClientsDialog({ open, onOpenChange, onDone }) {
  const [csv, setCsv] = useState("");
  const [running, setRunning] = useState(false);
  const [defaultType, setDefaultType] = useState("existing");

  // RFC-4180 friendly CSV line parser — handles quoted fields with commas.
  const splitCsvLine = (line) => {
    const out = [];
    let cur = "";
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQ) {
        if (ch === '"') {
          if (line[i + 1] === '"') { cur += '"'; i++; } else { inQ = false; }
        } else { cur += ch; }
      } else {
        if (ch === ',') { out.push(cur); cur = ""; }
        else if (ch === '"' && cur === "") { inQ = true; }
        else { cur += ch; }
      }
    }
    out.push(cur);
    return out.map((c) => c.trim());
  };

  // Header alias map → canonical backend keys (also flat billing_* the parser understands).
  const ALIASES = {
    "company name": "company_name", company: "company_name", name: "company_name",
    "contact person": "contact_person", contact: "contact_person", "primary contact": "contact_person",
    phone: "phone", "primary phone": "phone", mobile: "phone",
    "billing attention": "billing_attention", attention: "billing_attention",
    "billing address": "billing_line1", "billing address line 1": "billing_line1",
    "billing line1": "billing_line1", "billing line 1": "billing_line1",
    "billing street2": "billing_line2", "billing street 2": "billing_line2",
    "billing line2": "billing_line2", "billing line 2": "billing_line2",
    "billing city": "billing_city",
    "billing state": "billing_state",
    "billing phone": "billing_phone",
    "gst identification number (gstin)": "gst_number",
    "gst identification number": "gst_number",
    gstin: "gst_number", gst: "gst_number", "gst number": "gst_number", "gst no": "gst_number",
    "email id/ website": "email_or_website", "email id / website": "email_or_website",
    "email/website": "email_or_website", "email or website": "email_or_website",
    email: "email", "email id": "email", website: "website",
    "place of contact (with state code)": "place_of_supply",
    "place of contact": "place_of_supply", "place of supply": "place_of_supply",
    "state code": "state_code", "state name": "state_name",
    industry: "industry", location: "location", city: "location",
    type: "customer_type", "customer type": "customer_type", category: "customer_type",
    notes: "notes",
  };

  const parseCsv = (text) => {
    const lines = text.replace(/\r/g, "").split("\n").filter((l) => l.trim());
    if (lines.length === 0) return [];
    const header = splitCsvLine(lines[0]).map((h) => h.toLowerCase());
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = splitCsvLine(lines[i]);
      const row = {};
      header.forEach((h, idx) => {
        const val = (cols[idx] ?? "").trim();
        if (!val) return;
        const key = ALIASES[h] || h.replace(/\s+/g, "_");
        row[key] = val;
      });
      if (row.company_name) rows.push({ ...row, customer_type: row.customer_type || defaultType });
    }
    return rows;
  };

  const importNow = async () => {
    const clients = parseCsv(csv);
    if (clients.length === 0) {
      toast.error("No valid rows. CSV needs at least a `Company Name` column.");
      return;
    }
    setRunning(true);
    try {
      const { data } = await api.post("/clients/bulk-import", { clients });
      toast.success(`Imported ${data.inserted} clients · skipped ${data.skipped}`);
      setCsv("");
      onOpenChange(false);
      onDone?.();
    } catch (err) { toast.error(fmtError(err)); } finally { setRunning(false); }
  };

  const downloadTemplate = () => {
    const url = `${(typeof window !== "undefined" ? window.location.origin : "")}/api/import/template/clients`;
    window.open(url, "_blank");
  };

  const TEMPLATE_HEADERS = "Company Name,Contact Person,Phone,Billing Attention,Billing Address,Billing Street2,Billing City,Billing State,Billing Phone,GST Identification Number (GSTIN),Email ID/ Website,Place of Contact (With State Code)";
  const TEMPLATE_SAMPLE =
    `${TEMPLATE_HEADERS}\n` +
    `Acme Steel Industries,Rajeev Kumar,+91 98765 43210,Accounts Payable,"Plot 12, Hinjewadi Phase II",Near Infosys Gate 4,Pune,Maharashtra,+91 20 6655 4400,27ABCDE1234F1Z5,rajeev@acmesteel.in / www.acmesteel.in,27-Maharashtra\n` +
    `Crescent Power Plant,Sunita Iyer,+91 99876 54321,Finance Dept,"Survey 88, HITEC City",Madhapur,Hyderabad,Telangana,+91 40 4455 7788,36AABCC1234D1Z9,sunita@crescent.in / www.crescentpower.in,36-Telangana`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <UploadIcon className="h-5 w-5 text-primary" /> Bulk import clients
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          Paste your existing client spreadsheet as CSV. Required column: <code className="text-[11px]">Company Name</code>. Optional 12-column template:
          <code className="text-[11px] ml-1">Contact Person · Phone · Billing Attention · Billing Address · Billing Street2 · Billing City · Billing State · Billing Phone · GSTIN · Email ID/ Website · Place of Contact (With State Code)</code>.
          Wrap fields containing commas in <code>"double quotes"</code>. De-duped by GST first, then email + phone.
        </p>
        <div className="flex flex-wrap gap-2 items-center justify-between">
          <Button type="button" variant="outline" size="sm" className="rounded-2xl" onClick={downloadTemplate} data-testid="clients-template-download">
            Download CSV template
          </Button>
          <Button type="button" variant="ghost" size="sm" className="rounded-2xl text-xs" onClick={() => setCsv(TEMPLATE_SAMPLE)} data-testid="clients-template-paste-sample">
            Insert sample rows
          </Button>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-semibold">Default customer type (used when row doesn't specify)</Label>
          <Select value={defaultType} onValueChange={setDefaultType}>
            <SelectTrigger className="rounded-2xl h-10" data-testid="import-default-type"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-2xl">
              {TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Textarea
          rows={12}
          className="rounded-2xl font-mono text-xs"
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
          placeholder={TEMPLATE_SAMPLE}
          data-testid="clients-import-csv"
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-2xl">Cancel</Button>
          <Button onClick={importNow} disabled={running || !csv.trim()} className="rounded-2xl tap" data-testid="clients-import-run">
            {running ? "Importing…" : <><ExternalLink className="h-4 w-4 mr-1" /> Import</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
